'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  ListSubheader,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import './EditOrganisation.css';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import UploadIcon from '../../../../assets/Admin/Organisation/upload.svg';
import CloseIcon from '@mui/icons-material/Close';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import { Calendar } from 'iconsax-react';
import { axiosInstance } from '../../../../utils/tokenUtils';
import CalendarComponent from '../../../../components/Calendar/CalendarComponent';
import {
  trimString,
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
  sanitizePhoneDigits,
} from '../../../../components/Validation/Validation';

// --- COUNTRY CODE MAP AND UTILS (from Settings/CreateOrganisation) ---
const countryCodeMap = {
  AF: { code: '+93', name: 'Afghanistan' },
  AL: { code: '+355', name: 'Albania' },
  DZ: { code: '+213', name: 'Algeria' },
  AS: { code: '+1684', name: 'American Samoa' },
  AD: { code: '+376', name: 'Andorra' },
  AO: { code: '+244', name: 'Angola' },
  AI: { code: '+1264', name: 'Anguilla' },
  AG: { code: '+1268', name: 'Antigua and Barbuda' },
  AR: { code: '+54', name: 'Argentina' },
  AM: { code: '+374', name: 'Armenia' },
  AW: { code: '+297', name: 'Aruba' },
  AU: { code: '+61', name: 'Australia' },
  AT: { code: '+43', name: 'Austria' },
  AZ: { code: '+994', name: 'Azerbaijan' },
  BS: { code: '+1242', name: 'Bahamas' },
  BH: { code: '+973', name: 'Bahrain' },
  BD: { code: '+880', name: 'Bangladesh' },
  BB: { code: '+1246', name: 'Barbados' },
  BY: { code: '+375', name: 'Belarus' },
  BE: { code: '+32', name: 'Belgium' },
  BZ: { code: '+501', name: 'Belize' },
  BJ: { code: '+229', name: 'Benin' },
  BM: { code: '+1441', name: 'Bermuda' },
  BT: { code: '+975', name: 'Bhutan' },
  BO: { code: '+591', name: 'Bolivia' },
  BA: { code: '+387', name: 'Bosnia and Herzegovina' },
  BW: { code: '+267', name: 'Botswana' },
  BR: { code: '+55', name: 'Brazil' },
  BN: { code: '+673', name: 'Brunei' },
  BG: { code: '+359', name: 'Bulgaria' },
  BF: { code: '+226', name: 'Burkina Faso' },
  BI: { code: '+257', name: 'Burundi' },
  KH: { code: '+855', name: 'Cambodia' },
  CM: { code: '+237', name: 'Cameroon' },
  CA: { code: '+1', name: 'Canada' },
  CV: { code: '+238', name: 'Cape Verde' },
  KY: { code: '+1345', name: 'Cayman Islands' },
  CF: { code: '+236', name: 'Central African Republic' },
  TD: { code: '+235', name: 'Chad' },
  CL: { code: '+56', name: 'Chile' },
  CN: { code: '+86', name: 'China' },
  CO: { code: '+57', name: 'Colombia' },
  KM: { code: '+269', name: 'Comoros' },
  CG: { code: '+242', name: 'Congo' },
  CD: { code: '+243', name: 'Congo, Democratic Republic' },
  CK: { code: '+682', name: 'Cook Islands' },
  CR: { code: '+506', name: 'Costa Rica' },
  CI: { code: '+225', name: "Cote d'Ivoire" },
  HR: { code: '+385', name: 'Croatia' },
  CU: { code: '+53', name: 'Cuba' },
  CY: { code: '+357', name: 'Cyprus' },
  CZ: { code: '+420', name: 'Czech Republic' },
  DK: { code: '+45', name: 'Denmark' },
  DJ: { code: '+253', name: 'Djibouti' },
  DM: { code: '+1767', name: 'Dominica' },
  DO: { code: '+1', name: 'Dominican Republic' },
  EC: { code: '+593', name: 'Ecuador' },
  EG: { code: '+20', name: 'Egypt' },
  SV: { code: '+503', name: 'El Salvador' },
  GQ: { code: '+240', name: 'Equatorial Guinea' },
  ER: { code: '+291', name: 'Eritrea' },
  EE: { code: '+372', name: 'Estonia' },
  ET: { code: '+251', name: 'Ethiopia' },
  FK: { code: '+500', name: 'Falkland Islands' },
  FO: { code: '+298', name: 'Faroe Islands' },
  FJ: { code: '+679', name: 'Fiji' },
  FI: { code: '+358', name: 'Finland' },
  FR: { code: '+33', name: 'France' },
  GF: { code: '+594', name: 'French Guiana' },
  PF: { code: '+689', name: 'French Polynesia' },
  GA: { code: '+241', name: 'Gabon' },
  GM: { code: '+220', name: 'Gambia' },
  GE: { code: '+995', name: 'Georgia' },
  DE: { code: '+49', name: 'Germany' },
  GH: { code: '+233', name: 'Ghana' },
  GI: { code: '+350', name: 'Gibraltar' },
  GR: { code: '+30', name: 'Greece' },
  GL: { code: '+299', name: 'Greenland' },
  GD: { code: '+1473', name: 'Grenada' },
  GP: { code: '+590', name: 'Guadeloupe' },
  GU: { code: '+1671', name: 'Guam' },
  GT: { code: '+502', name: 'Guatemala' },
  GN: { code: '+224', name: 'Guinea' },
  GW: { code: '+245', name: 'Guinea-Bissau' },
  GY: { code: '+592', name: 'Guyana' },
  HT: { code: '+509', name: 'Haiti' },
  HN: { code: '+504', name: 'Honduras' },
  HK: { code: '+852', name: 'Hong Kong' },
  HU: { code: '+36', name: 'Hungary' },
  IS: { code: '+354', name: 'Iceland' },
  IN: { code: '+91', name: 'India' },
  ID: { code: '+62', name: 'Indonesia' },
  IR: { code: '+98', name: 'Iran' },
  IQ: { code: '+964', name: 'Iraq' },
  IE: { code: '+353', name: 'Ireland' },
  IL: { code: '+972', name: 'Israel' },
  IT: { code: '+39', name: 'Italy' },
  JM: { code: '+1876', name: 'Jamaica' },
  JP: { code: '+81', name: 'Japan' },
  JO: { code: '+962', name: 'Jordan' },
  KZ: { code: '+7', name: 'Kazakhstan' },
  KE: { code: '+254', name: 'Kenya' },
  KI: { code: '+686', name: 'Kiribati' },
  KW: { code: '+965', name: 'Kuwait' },
  KG: { code: '+996', name: 'Kyrgyzstan' },
  LA: { code: '+856', name: 'Laos' },
  LV: { code: '+371', name: 'Latvia' },
  LB: { code: '+961', name: 'Lebanon' },
  LS: { code: '+266', name: 'Lesotho' },
  LR: { code: '+231', name: 'Liberia' },
  LY: { code: '+218', name: 'Libya' },
  LI: { code: '+423', name: 'Liechtenstein' },
  LT: { code: '+370', name: 'Lithuania' },
  LU: { code: '+352', name: 'Luxembourg' },
  MO: { code: '+853', name: 'Macau' },
  MK: { code: '+389', name: 'Macedonia' },
  MG: { code: '+261', name: 'Madagascar' },
  MW: { code: '+265', name: 'Malawi' },
  MY: { code: '+60', name: 'Malaysia' },
  MV: { code: '+960', name: 'Maldives' },
  ML: { code: '+223', name: 'Mali' },
  MT: { code: '+356', name: 'Malta' },
  MH: { code: '+692', name: 'Marshall Islands' },
  MQ: { code: '+596', name: 'Martinique' },
  MR: { code: '+222', name: 'Mauritania' },
  MU: { code: '+230', name: 'Mauritius' },
  YT: { code: '+262', name: 'Mayotte' },
  MX: { code: '+52', name: 'Mexico' },
  FM: { code: '+691', name: 'Micronesia' },
  MD: { code: '+373', name: 'Moldova' },
  MC: { code: '+377', name: 'Monaco' },
  MN: { code: '+976', name: 'Mongolia' },
  ME: { code: '+382', name: 'Montenegro' },
  MS: { code: '+1664', name: 'Montserrat' },
  MA: { code: '+212', name: 'Morocco' },
  MZ: { code: '+258', name: 'Mozambique' },
  MM: { code: '+95', name: 'Myanmar' },
  NA: { code: '+264', name: 'Namibia' },
  NR: { code: '+674', name: 'Nauru' },
  NP: { code: '+977', name: 'Nepal' },
  NL: { code: '+31', name: 'Netherlands' },
  NC: { code: '+687', name: 'New Caledonia' },
  NZ: { code: '+64', name: 'New Zealand' },
  NI: { code: '+505', name: 'Nicaragua' },
  NE: { code: '+227', name: 'Niger' },
  NG: { code: '+234', name: 'Nigeria' },
  NU: { code: '+683', name: 'Niue' },
  NF: { code: '+672', name: 'Norfolk Island' },
  KP: { code: '+850', name: 'North Korea' },
  MP: { code: '+1670', name: 'Northern Mariana Islands' },
  NO: { code: '+47', name: 'Norway' },
  OM: { code: '+968', name: 'Oman' },
  PK: { code: '+92', name: 'Pakistan' },
  PW: { code: '+680', name: 'Palau' },
  PS: { code: '+970', name: 'Palestine' },
  PA: { code: '+507', name: 'Panama' },
  PG: { code: '+675', name: 'Papua New Guinea' },
  PY: { code: '+595', name: 'Paraguay' },
  PE: { code: '+51', name: 'Peru' },
  PH: { code: '+63', name: 'Philippines' },
  PL: { code: '+48', name: 'Poland' },
  PT: { code: '+351', name: 'Portugal' },
  PR: { code: '+1', name: 'Puerto Rico' },
  QA: { code: '+974', name: 'Qatar' },
  RE: { code: '+262', name: 'Reunion' },
  RO: { code: '+40', name: 'Romania' },
  RU: { code: '+7', name: 'Russia' },
  RW: { code: '+250', name: 'Rwanda' },
  KN: { code: '+1869', name: 'Saint Kitts and Nevis' },
  LC: { code: '+1758', name: 'Saint Lucia' },
  VC: { code: '+1784', name: 'Saint Vincent and the Grenadines' },
  WS: { code: '+685', name: 'Samoa' },
  SM: { code: '+378', name: 'San Marino' },
  ST: { code: '+239', name: 'Sao Tome and Principe' },
  SA: { code: '+966', name: 'Saudi Arabia' },
  SN: { code: '+221', name: 'Senegal' },
  RS: { code: '+381', name: 'Serbia' },
  SC: { code: '+248', name: 'Seychelles' },
  SL: { code: '+232', name: 'Sierra Leone' },
  SG: { code: '+65', name: 'Singapore' },
  SK: { code: '+421', name: 'Slovakia' },
  SI: { code: '+386', name: 'Slovenia' },
  SB: { code: '+677', name: 'Solomon Islands' },
  SO: { code: '+252', name: 'Somalia' },
  ZA: { code: '+27', name: 'South Africa' },
  KR: { code: '+82', name: 'South Korea' },
  SS: { code: '+211', name: 'South Sudan' },
  ES: { code: '+34', name: 'Spain' },
  LK: { code: '+94', name: 'Sri Lanka' },
  SD: { code: '+249', name: 'Sudan' },
  SR: { code: '+597', name: 'Suriname' },
  SZ: { code: '+268', name: 'Swaziland' },
  SE: { code: '+46', name: 'Sweden' },
  CH: { code: '+41', name: 'Switzerland' },
  SY: { code: '+963', name: 'Syria' },
  TW: { code: '+886', name: 'Taiwan' },
  TJ: { code: '+992', name: 'Tajikistan' },
  TZ: { code: '+255', name: 'Tanzania' },
  TH: { code: '+66', name: 'Thailand' },
  TL: { code: '+670', name: 'Timor-Leste' },
  TG: { code: '+228', name: 'Togo' },
  TK: { code: '+690', name: 'Tokelau' },
  TO: { code: '+676', name: 'Tonga' },
  TT: { code: '+1868', name: 'Trinidad and Tobago' },
  TN: { code: '+216', name: 'Tunisia' },
  TR: { code: '+90', name: 'Turkey' },
  TM: { code: '+993', name: 'Turkmenistan' },
  TC: { code: '+1649', name: 'Turks and Caicos Islands' },
  TV: { code: '+688', name: 'Tuvalu' },
  UG: { code: '+256', name: 'Uganda' },
  UA: { code: '+380', name: 'Ukraine' },
  AE: { code: '+971', name: 'United Arab Emirates' },
  GB: { code: '+44', name: 'United Kingdom' },
  US: { code: '+1', name: 'United States' },
  UY: { code: '+598', name: 'Uruguay' },
  UZ: { code: '+998', name: 'Uzbekistan' },
  VU: { code: '+678', name: 'Vanuatu' },
  VA: { code: '+39', name: 'Vatican City' },
  VE: { code: '+58', name: 'Venezuela' },
  VN: { code: '+84', name: 'Vietnam' },
  VG: { code: '+1284', name: 'Virgin Islands, British' },
  VI: { code: '+1340', name: 'Virgin Islands, U.S.' },
  WF: { code: '+681', name: 'Wallis and Futuna' },
  YE: { code: '+967', name: 'Yemen' },
  ZM: { code: '+260', name: 'Zambia' },
  ZW: { code: '+263', name: 'Zimbabwe' },
};

const getAllCountriesWithDialCodes = () => {
  return Object.entries(countryCodeMap)
    .map(([countryCode, data]) => ({
      countryCode,
      dialCode: data.code,
      name: data.name,
      displayText: `${data.code} (${data.name})`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const getFilteredCountries = search => {
  if (!search.trim()) {
    return getAllCountriesWithDialCodes();
  }
  const searchTerm = search.toLowerCase().trim();
  return getAllCountriesWithDialCodes().filter(
    country =>
      country.name.toLowerCase().includes(searchTerm) ||
      country.dialCode.includes(searchTerm) ||
      country.countryCode.toLowerCase().includes(searchTerm)
  );
};
// --- END COUNTRY CODE MAP AND UTILS ---

// Map common country aliases/abbreviations to official names used by /auth/countries
const countryAliasMap = {
  uae: 'United Arab Emirates',
  'u.a.e': 'United Arab Emirates',
  uk: 'United Kingdom',
  usa: 'United States',
  us: 'United States',
  ksa: 'Saudi Arabia',
};

const findCountryByNameOrAlias = (inputName, countries) => {
  if (!inputName || !Array.isArray(countries) || countries.length === 0) return null;
  const normalized = String(inputName).trim().toLowerCase();
  // 1) exact match (case-insensitive)
  let match = countries.find(c => (c.name || '').toLowerCase().trim() === normalized);
  if (match) return match;
  // 2) alias map
  const alias = countryAliasMap[normalized];
  if (alias) {
    match = countries.find(c => (c.name || '').toLowerCase().trim() === alias.toLowerCase());
    if (match) return match;
  }
  // 3) fuzzy contains match (best effort)
  match = countries.find(
    c =>
      (c.name || '').toLowerCase().includes(normalized) ||
      normalized.includes((c.name || '').toLowerCase())
  );
  return match || null;
};

const EditOrganisation = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  // Calendar state for DOB dropdown
  const [dobCalendarOpen, setDobCalendarOpen] = useState(false);

  const [canManageBankDetails, setCanManageBankDetails] = useState(false);
  const [canEditOrganisationCore, setCanEditOrganisationCore] = useState(true);

  // Store original data for comparison
  const [originalData, setOriginalData] = useState(null);

  // form state
  const [formData, setFormData] = useState({
    orgName: '',
    orgEmail: '',
    orgContactCode: '',
    orgContactNumber: '',
    orgAddress: '',
    orgCity: '',
    orgCountry: '',
    ownerName: '',
    ownerContactCode: '',
    ownerContactNumber: '',
    ownerGender: '',
    ownerDOB: null,
    bankName: '',
    bankAddress: '',
    accountHolderName: '',
    iban: '',
    document: null,
  });

  // Document upload state
  const [documents, setDocuments] = useState([{ id: 1, name: '', file: null }]);

  // Country/city state for dropdowns (mirrors Create page)
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = (field, value) => {
    switch (field) {
      case 'orgName': {
        const v = trimString(value);
        if (!v) return 'Organisation name is required';
        if (!isValidName(v)) return 'Name must contain only letters';
        return '';
      }
      case 'orgEmail': {
        const v = trimString(value).toLowerCase();
        if (!v) return 'Organisation email is required';
        if (!isValidEmail(v)) return 'Invalid email address';
        return '';
      }
      case 'orgContactNumber': {
        const d = sanitizePhoneDigits(value);
        if (!d) return 'Organisation contact number is required';
        if (!isValidPhoneNumber(d, 5, 15)) return 'Phone must be 5–15 digits';
        return '';
      }
      case 'orgAddress': {
        const v = trimString(value);
        if (!v) return 'Organisation address is required';
        return '';
      }
      case 'orgCountry': {
        const v = trimString(value);
        if (!v) return 'Organisation country is required';
        return '';
      }
      case 'orgCity': {
        const v = trimString(value);
        if (!v) return 'Organisation city is required';
        return '';
      }
      case 'ownerName': {
        const v = trimString(value);
        if (!v) return 'Owner name is required';
        if (!isValidName(v)) return 'Name must contain only letters';
        return '';
      }
      case 'ownerContactNumber': {
        const d = sanitizePhoneDigits(value);
        if (!d) return 'Owner contact number is required';
        if (!isValidPhoneNumber(d, 5, 15)) return 'Phone must be 5–15 digits';
        return '';
      }
      case 'ownerGender': {
        const v = trimString(value);
        if (!v) return 'Gender is required';
        return '';
      }
      default:
        return '';
    }
  };

  const handleValidatedChange = (field, rawValue) => {
    setFormData(prev => ({ ...prev, [field]: rawValue }));
    setTouched(prev => ({ ...prev, [field]: true }));
    setHasChanges(true);
    const message = validateField(field, rawValue);
    setErrors(prev => ({ ...prev, [field]: message }));
  };

  const isFormValidEdit = () => {
    const requiredFields = [
      'orgName',
      'orgEmail',
      'orgContactNumber',
      'orgAddress',
      'orgCountry',
      'orgCity',
      'ownerName',
      'ownerContactNumber',
      'ownerGender',
    ];
    for (const f of requiredFields) {
      const val = formData[f];
      if (validateField(f, val)) return false;
    }
    return true;
  };

  // Fetch current admin permissions (RBAC me)
  useEffect(() => {
    let mounted = true;
    const fetchMe = async () => {
      try {
        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        const perms = Array.isArray(data?.permissions) ? data.permissions : [];
        const roles = Array.isArray(data?.roles) ? data.roles : [];
        if (mounted) {
          setCanManageBankDetails(perms.includes('manage_bank_details'));
          const isFinanceOfficer = roles.includes('finance_officer');
          const canManageOrganisations = perms.includes('manage_organisations');
          setCanEditOrganisationCore(!isFinanceOfficer && canManageOrganisations);
        }
      } catch (_) {
        if (mounted) {
          setCanManageBankDetails(false);
          setCanEditOrganisationCore(false);
        }
      }
    };
    fetchMe();
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setCountryLoading(true);
      try {
        const response = await axiosInstance.get('/auth/countries');
        setCountries(response.data);
      } catch (error) {
        toast.error('Failed to load countries');
        setCountries([]);
      } finally {
        setCountryLoading(false);
      }
    };
    fetchCountries();
  }, []);

  // Fetch cities when country changes
  useEffect(() => {
    if (!formData.orgCountry) {
      setCities([]);
      return;
    }
    const selectedCountry = findCountryByNameOrAlias(formData.orgCountry, countries);
    if (!selectedCountry) {
      setCities([]);
      return;
    }
    setCityLoading(true);
    axiosInstance
      .get(`/auth/countries/${selectedCountry.id}/cities`)
      .then(res => setCities(res.data))
      .catch(() => {
        toast.error('Failed to load cities');
        setCities([]);
      })
      .finally(() => setCityLoading(false));
  }, [formData.orgCountry, countries]);

  // --- COUNTRY CODE DROPDOWN STATE AND HANDLERS ---
  const [orgCountryCodeDropdownOpen, setOrgCountryCodeDropdownOpen] = useState(false);
  const [orgCountryCodeSearch, setOrgCountryCodeSearch] = useState('');
  const orgCountryCodeDropdownRef = useRef(null);
  const orgCountryCodeSearchRef = useRef(null);

  const [ownerCountryCodeDropdownOpen, setOwnerCountryCodeDropdownOpen] = useState(false);
  const [ownerCountryCodeSearch, setOwnerCountryCodeSearch] = useState('');
  const ownerCountryCodeDropdownRef = useRef(null);
  const ownerCountryCodeSearchRef = useRef(null);

  const handleOrgCountryCodeDropdownToggle = () => {
    if (!canEditOrganisationCore) return;
    const newState = !orgCountryCodeDropdownOpen;
    setOrgCountryCodeDropdownOpen(newState);
    if (newState) {
      setTimeout(() => {
        if (orgCountryCodeSearchRef.current) {
          orgCountryCodeSearchRef.current.focus();
        }
      }, 100);
    } else {
      setOrgCountryCodeSearch('');
    }
  };

  const handleOwnerCountryCodeDropdownToggle = () => {
    if (!canEditOrganisationCore) return;
    const newState = !ownerCountryCodeDropdownOpen;
    setOwnerCountryCodeDropdownOpen(newState);
    if (newState) {
      setTimeout(() => {
        if (ownerCountryCodeSearchRef.current) {
          ownerCountryCodeSearchRef.current.focus();
        }
      }, 100);
    } else {
      setOwnerCountryCodeSearch('');
    }
  };

  const handleOrgCountryCodeSelect = dialCode => {
    if (!canEditOrganisationCore) return;
    setFormData(prev => ({ ...prev, orgContactCode: dialCode }));
    setOrgCountryCodeDropdownOpen(false);
    setOrgCountryCodeSearch('');
  };

  const handleOwnerCountryCodeSelect = dialCode => {
    if (!canEditOrganisationCore) return;
    setFormData(prev => ({ ...prev, ownerContactCode: dialCode }));
    setOwnerCountryCodeDropdownOpen(false);
    setOwnerCountryCodeSearch('');
  };

  const handleOrgCountryCodeSearchKeyDown = e => {
    if (e.key === 'Escape') {
      setOrgCountryCodeDropdownOpen(false);
      setOrgCountryCodeSearch('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const filteredCountries = getFilteredCountries(orgCountryCodeSearch);
      if (filteredCountries.length > 0) {
        handleOrgCountryCodeSelect(filteredCountries[0].dialCode);
      }
    }
  };

  const handleOwnerCountryCodeSearchKeyDown = e => {
    if (e.key === 'Escape') {
      setOwnerCountryCodeDropdownOpen(false);
      setOwnerCountryCodeSearch('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const filteredCountries = getFilteredCountries(ownerCountryCodeSearch);
      if (filteredCountries.length > 0) {
        handleOwnerCountryCodeSelect(filteredCountries[0].dialCode);
      }
    }
  };
  // --- END COUNTRY CODE DROPDOWN STATE AND HANDLERS ---

  // Search states for Country/City Select menus
  const [countryMenuSearch, setCountryMenuSearch] = useState('');
  const [cityMenuSearch, setCityMenuSearch] = useState('');

  // Load organization data
  useEffect(() => {
    const loadOrganizationData = async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(`/admin/organisations/${id}`);

        // Debug: Log the phone numbers to see their format
        console.log('Organization phone:', data.phone_no);
        console.log('Owner phone:', data.owner_phone_no);

        // Parse phone numbers to extract country code and number
        const parsePhoneNumber = phoneNo => {
          if (!phoneNo) return { code: '', number: '' };

          // Remove any spaces or special characters first
          const cleanPhone = phoneNo.replace(/\s+/g, '');

          // Try to match different phone number formats
          let code = '';
          let number = cleanPhone;

          // Pattern 1: +XX format (e.g., +91)
          const plusPattern = cleanPhone.match(/^\+(\d{1,4})/);
          if (plusPattern) {
            code = `+${plusPattern[1]}`;
            number = cleanPhone.replace(/^\+(\d{1,4})/, '');
            return { code, number };
          }

          // Pattern 2: XX format without + (e.g., 91)
          // Check for common country code patterns at the start
          const codePattern = cleanPhone.match(/^(\d{1,4})/);
          if (codePattern) {
            const potentialCode = codePattern[1];
            const remainingDigits = cleanPhone.substring(potentialCode.length);

            // If remaining digits are 7 or more, it's likely a country code
            if (remainingDigits.length >= 7) {
              code = `+${potentialCode}`;
              number = remainingDigits;
              return { code, number };
            }

            // Special case: If the number starts with common country codes
            const commonCodes = ['91', '971', '966', '974', '973', '965', '968', '971'];
            if (commonCodes.includes(potentialCode) && remainingDigits.length >= 7) {
              code = `+${potentialCode}`;
              number = remainingDigits;
              return { code, number };
            }
          }

          // If no pattern matches, return the whole number as the phone number
          return { code: '', number: cleanPhone };
        };

        const orgPhone = parsePhoneNumber(data.phone_no);
        const ownerPhone = parsePhoneNumber(data.owner_phone_no);

        // Debug: Log the parsed results
        console.log('Parsed org phone:', orgPhone);
        console.log('Parsed owner phone:', ownerPhone);

        // Populate form data
        setFormData({
          orgName: data.name || '',
          orgEmail: data.email || '',
          orgContactCode: orgPhone.code,
          orgContactNumber: orgPhone.number,
          orgAddress: data.address || '',
          orgCity: trimString(data.city || ''),
          orgCountry: trimString(data.country || ''),
          ownerName: data.owner_name || '',
          ownerContactCode: ownerPhone.code,
          ownerContactNumber: ownerPhone.number,
          ownerGender: data.owner_gender || '',
          ownerDOB: data.owner_dob ? new Date(data.owner_dob) : null,
          bankName: data.bank_details?.bank_name || '',
          bankAddress: data.bank_details?.bank_address || '',
          accountHolderName: data.bank_details?.account_holder_name || '',
          iban: data.bank_details?.iban || '',
          document: data.bank_details?.bank_document_url || null,
        });

        // Store original data
        setOriginalData({
          orgName: data.name || '',
          orgEmail: data.email || '',
          orgContactCode: orgPhone.code,
          orgContactNumber: orgPhone.number,
          orgAddress: data.address || '',
          orgCity: trimString(data.city || ''),
          orgCountry: trimString(data.country || ''),
          ownerName: data.owner_name || '',
          ownerContactCode: ownerPhone.code,
          ownerContactNumber: ownerPhone.number,
          ownerGender: data.owner_gender || '',
          ownerDOB: data.owner_dob ? new Date(data.owner_dob) : null,
          bankName: data.bank_details?.bank_name || '',
          bankAddress: data.bank_details?.bank_address || '',
          accountHolderName: data.bank_details?.account_holder_name || '',
          iban: data.bank_details?.iban || '',
          document: data.bank_details?.bank_document_url || null,
          documents: data.documents || [],
        });

        // Populate documents
        if (data.documents && data.documents.length > 0) {
          setDocuments(
            data.documents.map((doc, index) => ({
              id: index + 1,
              name: doc.type || `Document ${index + 1}`,
              file: doc.url,
              isExisting: true, // Flag to identify existing documents
            }))
          );
        } else {
          setDocuments([{ id: 1, name: '', file: null, isExisting: false }]);
        }
      } catch (error) {
        console.error('Error loading organization data:', error);

        toast.error('Failed to load organization data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadOrganizationData();
    }
  }, [id]);

  const handleChange = field => e => {
    handleValidatedChange(field, e.target.value);
  };

  const handleFileChange = e => {
    if (!canManageBankDetails) {
      toast.error("You don't have permission to update bank details");
      return;
    }
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, document: file }));
    setHasChanges(true);
  };

  const handleDocumentNameChange = (id, value) => {
    if (!canEditOrganisationCore) return;
    setDocuments(prev => prev.map(doc => (doc.id === id ? { ...doc, name: value } : doc)));
    setHasChanges(true);
  };

  const handleDocumentFileChange = (id, file) => {
    if (!canEditOrganisationCore) return;
    setDocuments(prev =>
      prev.map(doc => (doc.id === id ? { ...doc, file, isExisting: false } : doc))
    );
    setHasChanges(true);
  };

  const handleAddDocument = () => {
    if (!canEditOrganisationCore) return;
    const newId = documents.length > 0 ? Math.max(...documents.map(doc => doc.id)) + 1 : 1;
    setDocuments([...documents, { id: newId, name: '', file: null, isExisting: false }]);
    setHasChanges(true);
  };

  const handleRemoveDocument = id => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
    setHasChanges(true);
  };

  // Add this function to clear the bank document
  const handleClearBankDocument = () => {
    setFormData(prev => ({ ...prev, document: null }));
    setHasChanges(true);
  };

  const handleClearOrgDocument = id => {
    if (!canEditOrganisationCore) return;
    setDocuments(prev =>
      prev.map(doc => (doc.id === id ? { ...doc, name: '', file: null, isExisting: false } : doc))
    );
    setHasChanges(true);
  };

  // Function to check if there are actual changes
  const checkForChanges = () => {
    if (!originalData) return false;

    // Check form data changes
    const formDataChanged =
      formData.orgName !== originalData.orgName ||
      formData.orgEmail !== originalData.orgEmail ||
      formData.orgContactCode !== originalData.orgContactCode ||
      formData.orgContactNumber !== originalData.orgContactNumber ||
      formData.orgAddress !== originalData.orgAddress ||
      formData.orgCity !== originalData.orgCity ||
      formData.orgCountry !== originalData.orgCountry ||
      formData.ownerName !== originalData.ownerName ||
      formData.ownerContactCode !== originalData.ownerContactCode ||
      formData.ownerContactNumber !== originalData.ownerContactNumber ||
      formData.ownerGender !== originalData.ownerGender ||
      formData.bankName !== originalData.bankName ||
      formData.bankAddress !== originalData.bankAddress ||
      formData.accountHolderName !== originalData.accountHolderName ||
      formData.iban !== originalData.iban ||
      formData.document !== originalData.document;

    // Check date changes
    const originalDOB = originalData.ownerDOB
      ? originalData.ownerDOB.toISOString().split('T')[0]
      : null;
    const currentDOB = formData.ownerDOB ? formData.ownerDOB.toISOString().split('T')[0] : null;
    const dateChanged = originalDOB !== currentDOB;

    // Check document changes
    const originalDocs = originalData.documents || [];
    const currentDocs = documents.filter(doc => doc.name || doc.file);
    const documentsChanged =
      originalDocs.length !== currentDocs.length ||
      originalDocs.some((origDoc, index) => {
        const currentDoc = currentDocs[index];
        return !currentDoc || origDoc.name !== currentDoc.name || origDoc.file !== currentDoc.file;
      });

    return formDataChanged || dateChanged || documentsChanged;
  };

  // Update hasChanges whenever form data or documents change
  useEffect(() => {
    if (originalData) {
      const changed = checkForChanges();
      setHasChanges(changed);
    }
  }, [formData, documents, originalData]);

  const isFormValid =
    formData.orgName &&
    formData.orgEmail &&
    formData.orgContactCode &&
    formData.orgContactNumber &&
    formData.orgAddress &&
    formData.orgCity &&
    formData.orgCountry &&
    formData.ownerName &&
    formData.ownerContactCode &&
    formData.ownerContactNumber &&
    formData.ownerGender;

  // Enable save button if there are changes, even if form is not completely valid
  const canSave =
    hasChanges &&
    formData.orgName &&
    formData.orgEmail &&
    formData.orgContactCode &&
    formData.orgContactNumber &&
    formData.orgAddress &&
    formData.orgCity &&
    formData.orgCountry &&
    formData.ownerName &&
    formData.ownerContactCode &&
    formData.ownerContactNumber &&
    formData.ownerGender;

  // More lenient save logic - allow saving if there are changes and at least some basic info is filled
  const canSaveWithChanges =
    hasChanges &&
    formData.orgName &&
    formData.orgEmail &&
    formData.orgContactNumber &&
    formData.ownerName &&
    formData.ownerContactNumber;

  // Upload document to server and get URL
  const uploadDocument = async (file, documentType, orgName) => {
    const formData = new FormData();
    formData.append('file', file);
    // Use query params for org_name and document_type
    const params = new URLSearchParams({
      org_name: orgName,
      document_type: documentType,
    });
    try {
      const response = await axiosInstance.post(
        `/admin/organisation/upload-document?${params.toString()}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data.document_url;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw new Error(
        `Failed to upload ${documentType}: ${error.response?.data?.detail || error.message}`
      );
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!hasChanges) {
      toast.error('No changes to save');
      return;
    }

    // Clean and validate core fields
    const orgNameClean = trimString(formData.orgName);
    const orgEmailClean = trimString(formData.orgEmail).toLowerCase();
    const orgCodeClean = trimString(formData.orgContactCode || '+974');
    const orgPhoneDigits = sanitizePhoneDigits(formData.orgContactNumber);

    const ownerNameClean = trimString(formData.ownerName);
    const ownerCodeClean = trimString(formData.ownerContactCode || '+974');
    const ownerPhoneDigits = sanitizePhoneDigits(formData.ownerContactNumber);

    if (!orgNameClean || !isValidName(orgNameClean)) {
      toast.error('Please enter a valid organisation name (letters only)');
      return;
    }
    if (!orgEmailClean || !isValidEmail(orgEmailClean)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!isValidPhoneNumber(orgPhoneDigits, 5, 15)) {
      toast.error('Please enter a valid organisation phone number');
      return;
    }
    if (!ownerNameClean || !isValidName(ownerNameClean)) {
      toast.error('Please enter a valid owner name (letters only)');
      return;
    }
    if (!isValidPhoneNumber(ownerPhoneDigits, 5, 15)) {
      toast.error('Please enter a valid owner phone number');
      return;
    }

    setSaving(true);
    try {
      // Prepare the request payload
      const payload = {
        org_name: orgNameClean,
        org_email: orgEmailClean,
        org_phone_no: `${orgCodeClean}${orgPhoneDigits}`,
        org_address: trimString(formData.orgAddress),
        org_city: trimString(formData.orgCity),
        org_country: trimString(formData.orgCountry),
        owner_name: ownerNameClean,
        owner_phone_no: `${ownerCodeClean}${ownerPhoneDigits}`,
        owner_gender: trimString(formData.ownerGender),
        owner_dob: formData.ownerDOB ? format(formData.ownerDOB, 'yyyy-MM-dd') : null,
      };

      // Add bank details only if user has permission
      if (
        canManageBankDetails &&
        formData.bankName &&
        formData.accountHolderName &&
        formData.iban &&
        formData.bankAddress
      ) {
        payload.bank_name = trimString(formData.bankName);
        payload.bank_address = trimString(formData.bankAddress);
        payload.account_holder_name = trimString(formData.accountHolderName);
        payload.iban = trimString(formData.iban);

        // Upload bank document if provided (new file)
        if (formData.document && formData.document instanceof File) {
          payload.bank_document_url = await uploadDocument(
            formData.document,
            'bank_document',
            orgNameClean
          );
        } else if (formData.document && typeof formData.document === 'string') {
          // Keep existing bank document URL
          payload.bank_document_url = formData.document;
        }
        // If cleared, do not include bank_document_url
      }

      // Process organization documents
      const orgDocuments = {};
      for (const doc of documents) {
        if (trimString(doc.name) && doc.file) {
          if (doc.file instanceof File) {
            // Upload new file
            const documentUrl = await uploadDocument(doc.file, 'org_document', orgNameClean);
            orgDocuments[trimString(doc.name)] = documentUrl;
          } else if (typeof doc.file === 'string' && doc.isExisting) {
            // Keep existing document URL
            orgDocuments[trimString(doc.name)] = doc.file;
          }
        }
        // If cleared (doc.file is null), skip so it is excluded from payload
      }

      // Add organization documents to payload
      const originalDocsCount = Array.isArray(originalData?.documents)
        ? originalData.documents.length
        : 0;
      if (Object.keys(orgDocuments).length > 0) {
        // Send only current docs; removed ones are excluded
        payload.org_documents = orgDocuments;
      } else if (originalDocsCount > 0) {
        // If all docs were removed, send an empty object to clear server-side
        payload.org_documents = {};
      }

      // Make the API call
      await axiosInstance.patch(`/admin/organisation/${id}`, payload);

      toast.success('Organization updated successfully!');
      navigate(`/admin/organisation/${id}`);
    } catch (error) {
      console.error('Error updating organization:', error);
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to update organization';

      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBreadcrumbClick = () => {
    navigate(`/admin/organisation/${id}`);
  };

  return loading ? (
    <Box className="editorg-loader-wrapper">
      <CircularProgress />
    </Box>
  ) : (
    <Layout title="Klass Online | Edit Organisation">
      <Box className="editorg-page">
        <Box className="editorg-sticky-header">
          <AdminNavbar />
        </Box>
        <Box className="editorg-content-wrapper">
          <Box className="editorg-breadcrumb">
            <span className="editorg-breadcrumb-link" onClick={handleBreadcrumbClick}>
              Organization
            </span>
            <span className="editorg-breadcrumb-separator"> &gt;&gt; </span>
            <span className="editorg-breadcrumb-current">Edit</span>
          </Box>

          <Typography className="editorg-page-title">Edit Organisation</Typography>

          <form className="editorg-form" onSubmit={handleSubmit}>
            {/* Organisation Details */}
            <Box className="editorg-form-section">
              <Typography className="editorg-section-title">Organisation Details *</Typography>
              <Box className="editorg-fields-grid">
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Organisation Name</Typography>
                  <TextField
                    placeholder="Name"
                    value={formData.orgName}
                    onChange={handleChange('orgName')}
                    disabled={!canEditOrganisationCore}
                    error={touched.orgName && !!errors.orgName}
                    fullWidth
                    className="editorg-form-input"
                    variant="outlined"
                  />
                  {touched.orgName && errors.orgName && (
                    <Typography className="error-text">{errors.orgName}</Typography>
                  )}
                </Box>
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Organisation Email</Typography>
                  <TextField
                    placeholder="example@example.com"
                    value={formData.orgEmail}
                    onChange={handleChange('orgEmail')}
                    type="text"
                    error={touched.orgEmail && !!errors.orgEmail}
                    disabled={!canEditOrganisationCore}
                    fullWidth
                    className="editorg-form-input"
                    variant="outlined"
                  />
                  {touched.orgEmail && errors.orgEmail && (
                    <Typography className="error-text">{errors.orgEmail}</Typography>
                  )}
                </Box>
                <Box className="editorg-field-wrapper editorg-contact-field-wrapper">
                  <Typography className="editorg-field-label">Organisation Contact</Typography>
                  <Box className="editorg-contact-fields phone-input-container">
                    <div
                      className={`country-code-dropdown-container ${orgCountryCodeDropdownOpen ? 'open' : ''}`}
                      ref={orgCountryCodeDropdownRef}
                    >
                      <div
                        className={`country-code-display-button ${!canEditOrganisationCore ? 'disabled' : ''}`}
                        onClick={handleOrgCountryCodeDropdownToggle}
                        style={
                          !canEditOrganisationCore
                            ? { pointerEvents: 'none', opacity: 0.6, cursor: 'not-allowed' }
                            : {}
                        }
                      >
                        <span>{formData.orgContactCode || '+974'}</span>
                        <span className="dropdown-arrow">▼</span>
                      </div>
                      {orgCountryCodeDropdownOpen && (
                        <div className="country-code-dropdown">
                          <div className="country-code-search-container">
                            <input
                              ref={orgCountryCodeSearchRef}
                              type="text"
                              placeholder="Search countries..."
                              value={orgCountryCodeSearch}
                              onChange={e => setOrgCountryCodeSearch(e.target.value)}
                              className="country-code-search-input"
                              onKeyDown={handleOrgCountryCodeSearchKeyDown}
                            />
                          </div>
                          <div className="country-code-dropdown-content">
                            {getFilteredCountries(orgCountryCodeSearch).length > 0 ? (
                              getFilteredCountries(orgCountryCodeSearch).map((country, index) => (
                                <div
                                  key={`${country.dialCode}-${country.countryCode}-${index}`}
                                  className={`country-code-option ${formData.orgContactCode === country.dialCode ? 'selected' : ''}`}
                                  onClick={() => handleOrgCountryCodeSelect(country.dialCode)}
                                >
                                  {country.displayText}
                                </div>
                              ))
                            ) : (
                              <div className="country-code-no-results">No countries found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={formData.orgContactNumber}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= 15) {
                          handleValidatedChange('orgContactNumber', value);
                        }
                      }}
                      className="phone-number-input editorg-form-input editorg-contact-number"
                      placeholder="Phone Number"
                      maxLength={15}
                      disabled={!canEditOrganisationCore}
                    />
                    {touched.orgContactNumber && errors.orgContactNumber && (
                      <Typography className="error-text">{errors.orgContactNumber}</Typography>
                    )}
                  </Box>
                </Box>
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Organisation Address</Typography>
                  <TextField
                    placeholder="Street Name, State, PIN"
                    value={formData.orgAddress}
                    onChange={handleChange('orgAddress')}
                    error={touched.orgAddress && !!errors.orgAddress}
                    disabled={!canEditOrganisationCore}
                    fullWidth
                    className="editorg-form-input"
                    variant="outlined"
                  />
                  {touched.orgAddress && errors.orgAddress && (
                    <Typography className="error-text">{errors.orgAddress}</Typography>
                  )}
                </Box>
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Organisation Country</Typography>
                  <TextField
                    select
                    value={formData.orgCountry}
                    onChange={e => {
                      const v = e.target.value;
                      setFormData(prev => ({ ...prev, orgCountry: v, orgCity: '' }));
                      setTouched(prev => ({ ...prev, orgCountry: true }));
                      setErrors(prev => ({
                        ...prev,
                        orgCountry: validateField('orgCountry', v),
                        orgCity: '',
                      }));
                      setHasChanges(true);
                    }}
                    className="editorg-form-input"
                    variant="outlined"
                    fullWidth
                    error={touched.orgCountry && !!errors.orgCountry}
                    disabled={!canEditOrganisationCore}
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: {
                        PaperProps: {
                          className: 'editorg-select-menu-paper editorg-country-menu-paper',
                        },
                      },
                      onOpen: () => setCountryMenuSearch(''),
                      renderValue: selected =>
                        selected ? (
                          selected
                        ) : (
                          <span className="editorg-dropdown-placeholder">Select country</span>
                        ),
                    }}
                  >
                    <ListSubheader disableSticky className="editorg-menu-search-header">
                      <TextField
                        autoFocus
                        value={countryMenuSearch}
                        onChange={e => setCountryMenuSearch(e.target.value)}
                        placeholder="Search country..."
                        size="small"
                        fullWidth
                        onKeyDown={e => {
                          // prevent Select from capturing keystrokes
                          e.stopPropagation();
                        }}
                        className="editorg-menu-search-input"
                      />
                    </ListSubheader>
                    <MenuItem value="">Select country</MenuItem>
                    {countryLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      countries
                        .filter(country =>
                          country.name
                            .toLowerCase()
                            .includes((countryMenuSearch || '').toLowerCase())
                        )
                        .map(country => (
                          <MenuItem key={country.id} value={country.name}>
                            {country.name}
                          </MenuItem>
                        ))
                    )}
                  </TextField>
                  {touched.orgCountry && errors.orgCountry && (
                    <Typography className="error-text">{errors.orgCountry}</Typography>
                  )}
                </Box>
                {formData.orgCountry && (
                  <Box className="editorg-field-wrapper">
                    <Typography className="editorg-field-label">Organisation City</Typography>
                    <TextField
                      select
                      value={formData.orgCity}
                      onChange={e => {
                        const v = e.target.value;
                        setFormData(prev => ({ ...prev, orgCity: v }));
                        setTouched(prev => ({ ...prev, orgCity: true }));
                        setErrors(prev => ({ ...prev, orgCity: validateField('orgCity', v) }));
                        setHasChanges(true);
                      }}
                      className="editorg-form-input"
                      variant="outlined"
                      fullWidth
                      error={touched.orgCity && !!errors.orgCity}
                      disabled={!canEditOrganisationCore}
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: {
                          PaperProps: {
                            className: 'editorg-select-menu-paper editorg-city-menu-paper',
                          },
                        },
                        onOpen: () => setCityMenuSearch(''),
                        renderValue: selected =>
                          selected ? (
                            selected
                          ) : (
                            <span className="editorg-dropdown-placeholder">Select city</span>
                          ),
                      }}
                    >
                      <ListSubheader disableSticky className="editorg-menu-search-header">
                        <TextField
                          autoFocus
                          value={cityMenuSearch}
                          onChange={e => setCityMenuSearch(e.target.value)}
                          placeholder="Search city..."
                          size="small"
                          fullWidth
                          onKeyDown={e => {
                            e.stopPropagation();
                          }}
                          className="editorg-menu-search-input"
                        />
                      </ListSubheader>
                      <MenuItem value="">Select city</MenuItem>
                      {cityLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        cities
                          .filter(city =>
                            city.name.toLowerCase().includes((cityMenuSearch || '').toLowerCase())
                          )
                          .map(city => (
                            <MenuItem key={city.id} value={city.name}>
                              {city.name}
                            </MenuItem>
                          ))
                      )}
                    </TextField>
                    {touched.orgCity && errors.orgCity && (
                      <Typography className="error-text">{errors.orgCity}</Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Owner Details */}
            <Box className="editorg-form-section">
              <Typography className="editorg-section-title">Owner Details *</Typography>
              <Box className="editorg-fields-grid">
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Owner Name</Typography>
                  <TextField
                    placeholder="Name"
                    value={formData.ownerName}
                    onChange={handleChange('ownerName')}
                    disabled={!canEditOrganisationCore}
                    error={touched.ownerName && !!errors.ownerName}
                    fullWidth
                    className="editorg-form-input"
                    variant="outlined"
                  />
                  {touched.ownerName && errors.ownerName && (
                    <Typography className="error-text">{errors.ownerName}</Typography>
                  )}
                </Box>
                <Box className="editorg-field-wrapper editorg-contact-field-wrapper">
                  <Typography className="editorg-field-label">Owner Contact</Typography>
                  <Box className="editorg-contact-fields phone-input-container">
                    <div
                      className={`country-code-dropdown-container ${ownerCountryCodeDropdownOpen ? 'open' : ''}`}
                      ref={ownerCountryCodeDropdownRef}
                    >
                      <div
                        className={`country-code-display-button ${!canEditOrganisationCore ? 'disabled' : ''}`}
                        onClick={handleOwnerCountryCodeDropdownToggle}
                        style={
                          !canEditOrganisationCore
                            ? { pointerEvents: 'none', opacity: 0.6, cursor: 'not-allowed' }
                            : {}
                        }
                      >
                        <span>{formData.ownerContactCode || '+974'}</span>
                        <span className="dropdown-arrow">▼</span>
                      </div>
                      {ownerCountryCodeDropdownOpen && (
                        <div className="country-code-dropdown">
                          <div className="country-code-search-container">
                            <input
                              ref={ownerCountryCodeSearchRef}
                              type="text"
                              placeholder="Search countries..."
                              value={ownerCountryCodeSearch}
                              onChange={e => setOwnerCountryCodeSearch(e.target.value)}
                              className="country-code-search-input"
                              onKeyDown={handleOwnerCountryCodeSearchKeyDown}
                            />
                          </div>
                          <div className="country-code-dropdown-content">
                            {getFilteredCountries(ownerCountryCodeSearch).length > 0 ? (
                              getFilteredCountries(ownerCountryCodeSearch).map((country, index) => (
                                <div
                                  key={`${country.dialCode}-${country.countryCode}-${index}`}
                                  className={`country-code-option ${formData.ownerContactCode === country.dialCode ? 'selected' : ''}`}
                                  onClick={() => handleOwnerCountryCodeSelect(country.dialCode)}
                                >
                                  {country.displayText}
                                </div>
                              ))
                            ) : (
                              <div className="country-code-no-results">No countries found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={formData.ownerContactNumber}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= 15) {
                          handleValidatedChange('ownerContactNumber', value);
                        }
                      }}
                      className="phone-number-input editorg-form-input editorg-contact-number"
                      placeholder="Phone Number"
                      maxLength={15}
                      disabled={!canEditOrganisationCore}
                    />
                    {touched.ownerContactNumber && errors.ownerContactNumber && (
                      <Typography className="error-text">{errors.ownerContactNumber}</Typography>
                    )}
                  </Box>
                </Box>
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Gender</Typography>
                  <FormControl
                    fullWidth
                    className="editorg-form-input"
                    error={touched.ownerGender && !!errors.ownerGender}
                    disabled={!canEditOrganisationCore}
                  >
                    <Select
                      value={formData.ownerGender}
                      onChange={e => handleValidatedChange('ownerGender', e.target.value)}
                      displayEmpty
                      renderValue={selected =>
                        selected ? (
                          selected.charAt(0).toUpperCase() + selected.slice(1)
                        ) : (
                          <span className="editorg-dropdown-placeholder">Select Gender</span>
                        )
                      }
                      inputProps={{ 'aria-label': 'Gender' }}
                      className="editorg-select-input"
                    >
                      <MenuItem value="" disabled>
                        Select Gender
                      </MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  {touched.ownerGender && errors.ownerGender && (
                    <Typography className="error-text">{errors.ownerGender}</Typography>
                  )}
                </Box>
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Date of Birth</Typography>
                  <Box className="editorg-dob-field">
                    <TextField
                      value={formData.ownerDOB ? format(formData.ownerDOB, 'dd/MM/yyyy') : ''}
                      placeholder="DD/MM/YYYY"
                      fullWidth
                      className="editorg-form-input"
                      variant="outlined"
                      inputProps={{ readOnly: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Open calendar"
                              onClick={() => setDobCalendarOpen(true)}
                            >
                              <Calendar size="20" color="#949da6" variant="Outline" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <CalendarComponent
                      onDateSelect={date => {
                        setFormData(prev => ({ ...prev, ownerDOB: date }));
                        setHasChanges(true);
                        setDobCalendarOpen(false);
                      }}
                      selectedDate={formData.ownerDOB}
                      minDate={new Date(1900, 0, 1)}
                      maxDate={new Date()}
                      isOpen={dobCalendarOpen}
                      onClose={() => setDobCalendarOpen(false)}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Bank Details */}
            <Box className="editorg-form-section">
              <Typography className="editorg-section-title">Bank Details</Typography>
              {!canManageBankDetails && (
                <Box className="editorg-permission-banner">
                  <Typography className="editorg-permission-title">
                    No access to edit bank details
                  </Typography>
                  <Typography className="editorg-permission-text">
                    You do not have the required permission to update bank details.
                  </Typography>
                </Box>
              )}
              <FormHelperText className="editorg-optional-text">Optional</FormHelperText>
              <Box className="editorg-fields-grid">
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Bank Name</Typography>
                  <TextField
                    placeholder="Name"
                    value={formData.bankName}
                    onChange={e => {
                      handleValidatedChange('bankName', e.target.value);
                    }}
                    fullWidth
                    className="editorg-form-input"
                    variant="outlined"
                    disabled={!canManageBankDetails}
                  />
                </Box>
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Bank Address</Typography>
                  <TextField
                    placeholder="Address"
                    value={formData.bankAddress}
                    onChange={e => {
                      handleValidatedChange('bankAddress', e.target.value);
                    }}
                    fullWidth
                    className="editorg-form-input"
                    variant="outlined"
                    disabled={!canManageBankDetails}
                  />
                </Box>
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">Account Holder Name</Typography>
                  <TextField
                    placeholder="Full Name"
                    value={formData.accountHolderName}
                    onChange={e => {
                      handleValidatedChange('accountHolderName', e.target.value);
                    }}
                    fullWidth
                    className="editorg-form-input"
                    variant="outlined"
                    disabled={!canManageBankDetails}
                  />
                </Box>
                <Box className="editorg-field-wrapper">
                  <Typography className="editorg-field-label">IBAN Number</Typography>
                  <TextField
                    placeholder="XXXX"
                    value={formData.iban}
                    onChange={e => {
                      const raw = e.target.value || '';
                      const cleaned = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      setFormData(prev => ({ ...prev, iban: cleaned.slice(0, 34) }));
                      setHasChanges(true);
                    }}
                    fullWidth
                    className="editorg-form-input"
                    variant="outlined"
                    inputProps={{ maxLength: 34, pattern: '[A-Za-z0-9]*' }}
                    disabled={!canManageBankDetails}
                  />
                </Box>
                {/* Bank Document upload UI remains commented out; if re-enabled, ensure disabled={!canManageBankDetails} and guard handlers */}
              </Box>
            </Box>

            {/* Document Upload Section */}
            <Box className="editorg-form-section">
              <Typography className="editorg-section-title">Upload Relevant Document</Typography>
              <Box className="editorg-documents-container">
                {documents.map((doc, index) => (
                  <Box key={doc.id} className="editorg-document-row">
                    <Box className="editorg-document-field">
                      <Typography className="editorg-field-label">Document Name</Typography>
                      <TextField
                        placeholder="Name"
                        value={doc.name}
                        onChange={e => handleDocumentNameChange(doc.id, e.target.value)}
                        fullWidth
                        className="editorg-form-input"
                        variant="outlined"
                        disabled={!canEditOrganisationCore}
                      />
                    </Box>
                    <Box className="editorg-document-field">
                      <Typography className="editorg-field-label">Upload</Typography>
                      <Box className="editorg-file-upload-wrapper">
                        <input
                          id={`document-upload-${doc.id}`}
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={e => handleDocumentFileChange(doc.id, e.target.files[0])}
                          hidden
                          disabled={!canEditOrganisationCore}
                        />
                        <Box className="editorg-document-upload-box">
                          {doc.file ? (
                            <Box className="editorg-uploaded-file">
                              <FileIcon className="editorg-file-icon" />
                              <Typography className="editorg-file-name">
                                {doc.file instanceof File
                                  ? doc.file.name
                                  : doc.isExisting
                                    ? doc.name || 'Document'
                                    : 'Document'}
                              </Typography>
                              <Box className="editorg-file-actions">
                                <a
                                  href={
                                    doc.file instanceof File
                                      ? URL.createObjectURL(doc.file)
                                      : doc.file
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                  className="editorg-file-view-link"
                                >
                                  View
                                </a>
                              </Box>
                            </Box>
                          ) : (
                            <label
                              htmlFor={`document-upload-${doc.id}`}
                              className={`editorg-upload-label ${!canEditOrganisationCore ? 'disabled' : ''}`}
                              style={
                                !canEditOrganisationCore
                                  ? { pointerEvents: 'none', opacity: 0.6, cursor: 'not-allowed' }
                                  : {}
                              }
                            >
                              <Typography className="editorg-upload-placeholder">Upload</Typography>
                              <img src={UploadIcon} alt="Upload" className="editorg-upload-icon" />
                            </label>
                          )}
                        </Box>
                        <IconButton
                          className="editorg-remove-document"
                          aria-label="Clear file"
                          onClick={() => handleClearOrgDocument(doc.id)}
                          disabled={!canEditOrganisationCore}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                ))}
                <Box className="editorg-add-document-container">
                  <Button
                    className="editorg-add-document-btn"
                    onClick={handleAddDocument}
                    startIcon={<span>+</span>}
                    disabled={!canEditOrganisationCore}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box className="editorg-actions-bar">
              {hasChanges && (
                <Typography className="editorg-changes-indicator">
                  You have unsaved changes
                </Typography>
              )}
              <Button className="editorg-cancel-btn" type="button" onClick={handleBreadcrumbClick}>
                Cancel
              </Button>
              <Button
                className={`editorg-save-btn ${hasChanges ? 'has-changes' : ''}`}
                type="submit"
                disabled={saving || !hasChanges}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Layout>
  );
};

export default EditOrganisation;
